import { HttpApiService } from "@/shared/services/httpApi.service";

/**
 * Corridor availability of the payments runtime — coloca-nest `InternalPaymentCorridorsController` (`GET
 * /internal/payments/corridors`, `payments:read`).
 *
 * Unlike `/internal/payments/routes`, which is `userTypes: [INTERNAL]` and returns raw config rows including
 * `provider_code`, this one is reachable by organization members and carries no provider identity — it exists
 * so a UI can enable or disable a country/rail instead of letting the user discover the gap as a `no_route`
 * (400) or `no_fee_config` (404) at create time.
 *
 * Three verdicts, and the differences matter:
 *
 * - A corridor with **no active route** is ABSENT from the list (there is no `no_route` entry);
 * - A corridor whose rail an operator switched off (globally or for this organization) comes back `available:
 *   false` with `unavailable_reason: "rail_disabled"` — temporary by nature, so the UI says "temporarily
 *   unavailable" instead of "not enabled for your organization";
 * - A corridor with a route but without resolvable fees comes back `available: false` with `unavailable_reason:
 *   "no_fee_config"`.
 * - A PAYOUT corridor whose rail cannot pay even its smallest amount right now comes back `available: false`
 *   with `unavailable_reason: "provider_balance_low"` — temporary, it clears once the rail is funded, so the
 *   UI says "temporarily disabled" and points to another rail (Bre-B for Transfiya). A larger amount the rail
 *   cannot pay is refused at create with 409 `provider_balance_insufficient`.
 *
 * Commissions can be organization-specific, so the answer is per organization: members are pinned to their
 * own by the server, INTERNAL readers must pass the org they are looking at.
 */

export type PaymentDirection = "PAYIN" | "PAYOUT";

export type CorridorUnavailableReason = "no_fee_config" | "rail_disabled" | "provider_balance_low";

export type LimitPayerType = "PERSON" | "COMPANY";

/**
 * Per-transaction bounds for one payer kind, already resolved for the organization. Decimal strings; null =
 * none.
 */
export interface CorridorAmountLimit {
  payer_type: LimitPayerType;
  min_amount: string | null;
  max_amount: string | null;
}

export interface PaymentCorridor {
  rail: string;
  /** ISO-3166 alpha-3. */
  country: string;
  currency: string;
  direction: PaymentDirection;
  available: boolean;
  unavailable_reason: CorridorUnavailableReason | null;
  /** One entry per payer kind (PERSON, COMPANY). Optional so a backend without it still parses. */
  limits?: CorridorAmountLimit[];
}

export interface PaymentCorridorsResponse {
  corridors: PaymentCorridor[];
}

const CORRIDORS_URL = "/internal/payments/corridors";

export const PaymentCorridorsService = {
  async list(organizationId?: string): Promise<PaymentCorridorsResponse> {
    const query = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : "";
    const { data } = await HttpApiService.get<PaymentCorridorsResponse>(`${CORRIDORS_URL}${query}`);
    return data;
  },
};
